import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ConfigProvider } from './contexts/ConfigContext';
import { DynamicSettingsProvider } from './contexts/DynamicSettingsContext';
import { useAuth } from './hooks/useAuth';
import { usePermission } from './hooks/usePermission';
import Layout from './components/Layout/Layout';
import LoginPage from './pages/Auth/LoginPage';
import DashboardPage from './pages/Dashboard/DashboardPage';
import ProfilePage from './pages/Account/ProfilePage';
import SettingsPage from './pages/Settings/SettingsPage';
import { SettingsProvider } from './contexts/SettingsContext';
import { ToastProvider } from './components/UI/ToastContainer';
import IngredientsPage from './pages/Setup/IngredientsPage';
import DietsPage from './pages/Setup/DietsPage';
import MeasureUnitsPage from './pages/Setup/MeasureUnitsPage';
import PricesPage from './pages/Setup/PricesPage';
import CountriesPage from './pages/Setup/CountriesPage';
import CitiesPage from './pages/Setup/CitiesPage';
import IngredientFamiliesPage from './pages/Setup/IngredientFamiliesPage';
import LookupTablePage from './pages/Setup/LookupTablePage';
import RouteDeliveriesPage from './pages/Setup/RouteDeliveriesPage';
import MenusPage from './pages/Setup/MenusPage';
import MenuManagementPage from './pages/Setup/MenuManagementPage';
import RecipesPage from './pages/Setup/RecipesPage';
import RecipePlatesPage from './pages/Setup/RecipePlatesPage';
import PriceDietPricingPage from './pages/Setup/PriceDietPricingPage';
import MenuDetailsViewPage from './pages/Setup/MenuDetailsViewPage';

import ClientsPage from './pages/Clients/ClientsPage';
import ReportsPage from './pages/Reports/ReportsPage';
import PaymentsPage from './pages/Payments/PaymentsPage';
import ReceiptsPage from './pages/Receipts/ReceiptsPage';
import BackupPage from './pages/Backup/BackupPage';
import { AdminDashboard } from './pages/Admin/AdminDashboard';
import LoadingSpinner from './components/UI/LoadingSpinner';

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Protected Page Route Component - checks page-level permissions
const ProtectedPageRoute: React.FC<{ children: React.ReactNode; page: string }> = ({ children, page }) => {
  const { canView } = usePermission();

  const pageNameUpper = page.toUpperCase();

  // Special handling for Reports page - check for any report sub-permissions
  let hasAccess = false;
  if (pageNameUpper === 'REPORTS') {
    hasAccess =
      canView('REPORTS') ||
      canView('REPORTS_OVERVIEW') ||
      canView('REPORTS_CLIENTS') ||
      canView('REPORTS_REVENUE') ||
      canView('REPORTS_INGREDIENTS') ||
      canView('REPORTS_DIETS') ||
      canView('REPORTS_CHEF_COUNT') ||
      canView('REPORTS_FILING');
  } else {
    hasAccess = canView(pageNameUpper);
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-2">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

// Public Route Component (redirect to dashboard if authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <ConfigProvider>
      <DynamicSettingsProvider>
        <AuthProvider>
          <SettingsProvider>
            <ToastProvider>
              <div className="App">
                <Routes>
                {/* Public Routes */}
                <Route
                  path="/login"
                  element={
                    <PublicRoute>
                      <LoginPage />
                    </PublicRoute>
                  }
                />

                {/* Protected Routes */}
                <Route
                  path="/*"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Routes>
                          <Route path="/" element={<Navigate to="/dashboard" replace />} />
                          <Route path="/dashboard" element={<DashboardPage />} />

                          {/* Account */}
                          <Route path="/profile" element={<ProfilePage />} />

                          {/* Admin Dashboard */}
                          <Route path="/admin" element={<AdminDashboard />} />

                          {/* Admin Settings */}
                          <Route path="/settings" element={<SettingsPage />} />

                          {/* Setup Routes */}
                          <Route path="/setup/ingredient-families" element={<ProtectedPageRoute page="INGREDIENT_FAMILIES"><IngredientFamiliesPage /></ProtectedPageRoute>} />
                          <Route path="/setup/lookup-table" element={<ProtectedPageRoute page="LOOKUP_TABLE"><LookupTablePage /></ProtectedPageRoute>} />
                          <Route path="/setup/route-deliveries" element={<ProtectedPageRoute page="ROUTE_DELIVERIES"><RouteDeliveriesPage /></ProtectedPageRoute>} />
                          <Route path="/setup/menus" element={<ProtectedPageRoute page="MENUS"><MenusPage /></ProtectedPageRoute>} />
                          <Route path="/setup/menus/:id/manage" element={<ProtectedPageRoute page="MENUS"><MenuManagementPage /></ProtectedPageRoute>} />
                          <Route path="/setup/recipes" element={<ProtectedPageRoute page="RECIPES"><RecipesPage /></ProtectedPageRoute>} />
                          <Route path="/setup/recipe-plates" element={<ProtectedPageRoute page="RECIPE_PLATES"><RecipePlatesPage /></ProtectedPageRoute>} />

                          <Route path="/setup/ingredients" element={<ProtectedPageRoute page="INGREDIENTS"><IngredientsPage /></ProtectedPageRoute>} />
                          <Route path="/setup/diets" element={<ProtectedPageRoute page="DIETS"><DietsPage /></ProtectedPageRoute>} />
                          <Route path="/setup/diets/:dietId/menu-details" element={<ProtectedPageRoute page="DIETS"><MenuDetailsViewPage /></ProtectedPageRoute>} />
                          <Route path="/setup/measure-units" element={<ProtectedPageRoute page="MEASURE_UNITS"><MeasureUnitsPage /></ProtectedPageRoute>} />
                          <Route path="/setup/prices" element={<ProtectedPageRoute page="PRICES"><PricesPage /></ProtectedPageRoute>} />
                          <Route path="/setup/prices/:id/manage" element={<ProtectedPageRoute page="PRICES"><PriceDietPricingPage /></ProtectedPageRoute>} />
                          <Route path="/setup/countries" element={<ProtectedPageRoute page="COUNTRIES"><CountriesPage /></ProtectedPageRoute>} />
                          <Route path="/setup/cities" element={<ProtectedPageRoute page="CITIES"><CitiesPage /></ProtectedPageRoute>} />

                          {/* Client Routes */}
                          <Route path="/clients" element={<ProtectedPageRoute page="clients"><ClientsPage /></ProtectedPageRoute>} />

                          {/* Reports Routes */}
                          <Route path="/reports" element={<ProtectedPageRoute page="reports"><ReportsPage /></ProtectedPageRoute>} />

                          {/* Payments Routes */}
                          <Route path="/payments" element={<ProtectedPageRoute page="payments"><PaymentsPage /></ProtectedPageRoute>} />

                          {/* Receipts Routes */}
                          <Route path="/receipts" element={<ProtectedPageRoute page="receipts"><ReceiptsPage /></ProtectedPageRoute>} />

                          {/* Backup Routes */}
                          <Route path="/backup" element={<ProtectedPageRoute page="backup"><BackupPage /></ProtectedPageRoute>} />

                          {/* Catch all route */}
                          <Route path="*" element={<Navigate to="/dashboard" replace />} />
                        </Routes>
                      </Layout>
                    </ProtectedRoute>
                  }
                />
              </Routes>
              </div>
            </ToastProvider>
          </SettingsProvider>
        </AuthProvider>
      </DynamicSettingsProvider>
    </ConfigProvider>
  );
};

export default App;
